import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import Raven from 'raven-js';
import { useCallback } from 'react';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { BaseRequestAdminModal } from './BaseRequestAdminModal';
import { RequestAdminInstallModal } from './RequestAdminInstallModal';
import { useAdminEmail, useAdmins } from './hooks/useAdmins';
import { addInstallRequest } from './utils/installRequestTracking';
import { jsx as _jsx } from "react/jsx-runtime";
export const getRoleI18nKey = (role, portalScopes) => {
  let key = role.toLowerCase().replace(/ /g, '-');
  if (portalScopes) {
    if (portalScopes.includes('sales-nav-access')) {
      key += '-sales';
    } else if (portalScopes.includes('service-access')) {
      key += '-service';
    }
  }
  return `detailsPage.roles.${key}`;
};
export function RequestPermissionsModal({
  legacyListingId,
  missingUserRoles = [],
  portalScopes,
  listingId,
  onClose,
  onOpenComplete,
  onSubmit
}) {
  const {
    trackInteraction
  } = useDetailsPageContext();
  const {
    admins: adminsData,
    adminsLoading,
    adminsError
  } = useAdmins();
  const {
    emailAdmin,
    loading: emailLoading
  } = useAdminEmail();

  // Extract admins array from the query response
  const admins = Array.isArray(adminsData) ? adminsData : (adminsData === null || adminsData === void 0 ? void 0 : adminsData.admins) || [];
  const handleEmailAdmin = useCallback(async (adminUserId, listingIdParam, adminInstall, emailPermissionsList) => {
    const result = await emailAdmin(adminUserId, listingIdParam || legacyListingId, adminInstall, emailPermissionsList);
    if (result.success) {
      // Track successful email send
      trackInteraction({
        action: 'admin email sent successfully'
      });
      // Add this listingId to localStorage to track the request
      addInstallRequest(listingId);
      // Show success alert
      FloatingAlertStore.addAlert({
        type: 'success',
        titleText: unescapedText('detailsPage.productDetailsCard.cta.requestAdminSuccess.titleText'),
        message: unescapedText('detailsPage.productDetailsCard.cta.requestAdminSuccess.message')
      });
    }
    return result;
  }, [emailAdmin, trackInteraction, legacyListingId, listingId]);
  const onlyMissingRoles = missingUserRoles.find(role => isValidI18nKey(getRoleI18nKey(role, portalScopes))) === undefined;
  const formatPermissions = useCallback(asString => {
    return missingUserRoles.map(role => {
      const roleKey = getRoleI18nKey(role, portalScopes);
      if (!isValidI18nKey(roleKey)) {
        Raven.captureMessage(`Invalid role translation - ${role}`, {
          level: 'warning',
          extra: {
            role
          }
        });
        return null;
      }
      return asString ? I18n.text(roleKey) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: roleKey
      }, role);
    });
  }, [missingUserRoles, portalScopes]);
  const emailPermissionsList = formatPermissions(true).filter(role => !!role);
  const modalPermissionsList = formatPermissions(false);
  if (emailPermissionsList.length === 0) {
    Raven.captureMessage('Empty list of role translations', {
      level: 'error',
      extra: {
        missingUserRoles
      }
    });
  }
  const commonProps = {
    listingId: legacyListingId,
    onClose,
    onOpenComplete,
    onSubmit,
    admins,
    emailAdmin: handleEmailAdmin,
    loading: adminsLoading || emailLoading,
    error: adminsError ? /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.error.ADMIN_FETCH.message"
      })
    }) : undefined
  };
  if (onlyMissingRoles) {
    return /*#__PURE__*/_jsx(RequestAdminInstallModal, Object.assign({}, commonProps));
  }
  return /*#__PURE__*/_jsx(BaseRequestAdminModal, Object.assign({}, commonProps, {
    adminInstall: false,
    emailPermissionsList: emailPermissionsList,
    help: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.permissionsModal.help"
    }),
    modalHeader: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.permissionsModal.header"
    }),
    modalPermissionsList: modalPermissionsList,
    subheader: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.permissionsModal.subheader"
    })
  }));
}